import type { AdditionalFiltersFormatted, ICandidate, IStatusTransition } from "@bbj-types/candidate";
import type { ICerDates } from "@bbj-types/candidate/cer";
import type { IAuthor } from "@bbj-types/user";
import type { ObjectId } from "@bbj-types/utils";
import type {
  AdvancedFieldTypes,
  AdvancedFilterConditions,
  ShowActions,
} from "@candidate/dashboardV2/header/filters/types";
import { useSuspense } from "@root/api/tools/axios";
import type {
  Gender,
  MaritalStatus,
  Nationality,
  PESituation,
  ResidencePermit,
} from "@root/utils/constants/adminInformation";
import type { IDateRange } from "@styleguide";
import { useCallback } from "react";
import { axiosCandidateInstanceV2, axiosV2 } from "./axios";

export interface IGetCandidatesDashboardOptions {
  limit: number;
  skip: number;
  sort?: {
    sortDeadlineDate?: 1;
    sortExecutionDate?: 1 | -1;
    lastCEEndDate?: 1;
    lastName: 1;
    firstName: 1;
  };
}

interface IGetDashboardAdvancedFilterEmpty {
  condition: AdvancedFilterConditions.EMPTY;
  filterName: string;
}

interface IGetDashboardAdvancedFilterEqual {
  condition: AdvancedFilterConditions.EQUAL | AdvancedFilterConditions.NOT_EQUAL;
  filterName: string;
  type: AdvancedFieldTypes;
  value: string[] | number[] | number | string | boolean | Date;
}

interface IGetDashboardAdvancedFilterContain {
  condition: AdvancedFilterConditions.CONTAIN;
  filterName: string;
  type: AdvancedFieldTypes.TEXT | AdvancedFieldTypes.LIST_MULTIPLE | AdvancedFieldTypes.AUTHOR;
  value: string[] | string;
}

export type TGetDashboardAdvancedFilter =
  | IGetDashboardAdvancedFilterEmpty
  | IGetDashboardAdvancedFilterEqual
  | IGetDashboardAdvancedFilterContain;

export interface IGetDashboardFilters {
  actionPeriod?: IDateRange;
  actionTypes?: string[];
  additionalSections?: AdditionalFiltersFormatted;
  "address.isQPV"?: boolean;
  advancedFilters?: TGetDashboardAdvancedFilter[];
  "applicationFiles.jobs"?: string[];
  authors?: ObjectId[];
  authorsActions?: ObjectId[];
  authorsSince?: Date;
  birthDate?: IDateRange;
  cafNumber?: string;
  cerOngoing?: boolean;
  cerEnd?: IDateRange;
  cerStart?: IDateRange;
  cguStatus?: string[];
  "coachingDates.startDate"?: IDateRange;
  coachingStep?: string[];
  driverLicenses?: string[];
  gender?: Gender[];
  hideAutoGeneratedActions?: true;
  isAnonymized?: boolean;
  isArchived?: boolean;
  isDeleted?: boolean;
  multiSearch?: string;
  optInEmail?: boolean;
  optInPhone?: boolean;
  place?: string[];
  showActions?: ShowActions;
  source?: string[];
  status?: string[];
  team?: string[];
  objectives?: ObjectId[];
  poleEmploiSituation?: PESituation[];
  poleEmploiRegistrationDate?: IDateRange;
  nationality?: Nationality[];
  residencePermit?: ResidencePermit[];
  marriedSituation?: MaritalStatus;
}

export type IGetCandidatesDashboardBody = IGetDashboardFilters & IGetCandidatesDashboardOptions;

interface Action {
  _id: string;
  display: boolean;
  isAutoGenerate: boolean;
  status: "done" | "dismissed" | "todo" | "missed";
  type: string;
  contactChannel?: string;
  owner?: IAuthor;
  title?: string;
}

export interface LastAction extends Action {
  executionDate: Date;
}

export interface NextAction extends Action {
  deadlineDate: Date;
}

type IDashboardBasicCandidate = Pick<
  ICandidate,
  | "_id"
  | "address"
  | "coachingStep"
  | "firstName"
  | "isArchived"
  | "lastName"
  | "optInEmail"
  | "optInPhone"
  | "organisation"
  | "mail"
  | "phone"
  | "compPhone"
  | "profileId"
  | "status"
  | "isEmailValid"
> & { team: ObjectId };

interface IDashboardCandidate extends IDashboardBasicCandidate {
  authors: IAuthor[];
  lastStatusTransition: IStatusTransition | null;
  lastCE: ICerDates | null;
}

interface ICandidateDashboardActions {
  lastAction: LastAction;
  nextAction: NextAction[];
}

interface ICandidateDashboardMessage {
  newMessagesCount: number;
  messageError: boolean;
}

export type ICandidateDashboardData = IDashboardCandidate & ICandidateDashboardActions & ICandidateDashboardMessage;

export interface ICandidateDashboardResult {
  count: number;
  data: ICandidateDashboardData[];
  limit: number;
  skip: number;
  total: number;
  totalIds: ObjectId[];
}

export const useGetCandidatesDashboard = () => {
  const [state, execute, cancel] = axiosV2<ICandidateDashboardResult, IGetCandidatesDashboardBody, unknown>(
    {
      url: "/candidates/dashboard",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    (filters: IGetCandidatesDashboardBody) => {
      return execute({ data: filters });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};

export const usePostCandidatesDashboardSuspense = (filters?: IGetCandidatesDashboardBody) => {
  return useSuspense(
    () =>
      axiosCandidateInstanceV2.post<ICandidateDashboardResult>("/dashboard", filters, {
        withCredentials: true,
      }),
    [filters]
  );
};
